import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { adMediaUrl, getAdPositionMeta } from '../utils/adFormats';

/**
 * Shared ad renderer: correct aspect box, no creative-covering overlay.
 */
const AdUnit = ({
  ad,
  variant = 'sidebar', // sidebar | header | mobile | feed | detail
  className = '',
  onShown
}) => {
  const ref = useRef(null);
  const viewed = useRef(false);
  const meta = getAdPositionMeta(ad.position);
  const src = adMediaUrl(config.API_URL, ad);

  useEffect(() => {
    if (!ref.current || !ad?._id) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || viewed.current) return;
        viewed.current = true;
        axios.post(`${config.API_URL}/api/ads/${ad._id}/view`).catch(() => {});
        if (onShown) onShown(ad);
      },
      { threshold: 0.4 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ad, onShown]);

  const handleClick = async () => {
    try {
      await axios.post(`${config.API_URL}/api/ads/${ad._id}/click`);
    } catch (_) {
      /* ignore */
    }
    window.open(ad.link, '_blank', 'noopener,noreferrer');
  };

  if (!src) return null;

  const aspect =
    variant === 'header' || variant === 'feed'
      ? '970 / 250'
      : variant === 'mobile'
        ? '16 / 5'
        : variant === 'detail'
          ? (meta?.aspect || '728 / 90')
          : null; // sidebar: natural height up to 300×600

  return (
    <article
      ref={ref}
      className={`ad-unit ad-unit--${variant} ${className}`.trim()}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      role="link"
      tabIndex={0}
      aria-label={`Reklama: ${ad.title}`}
    >
      <span className="ad-unit__badge">Reklama</span>
      <div
        className="ad-unit__frame"
        style={aspect ? { aspectRatio: aspect } : undefined}
      >
        {ad.mediaType === 'video' ? (
          <video
            className="ad-unit__media"
            src={src}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img className="ad-unit__media" src={src} alt={ad.title} loading="lazy" />
        )}
      </div>
      {ad.title ? <p className="ad-unit__caption">{ad.title}</p> : null}
    </article>
  );
};

export function useAds(position, limit = 10) {
  const [ads, setAds] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${config.API_URL}/api/ads`, { params: { position, limit } })
      .then((res) => {
        if (!cancelled) setAds(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setAds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [position, limit]);

  return ads;
}

export default AdUnit;
